import { observable } from 'rewire-core';

export type EntitySort<T>     = (e1: T, e2: T) => number;
export type PollingCallbackFn = () => void;

interface IEntityPollingModelState<T> {
  entities: T[];
}

export abstract class EntityPollingModel<T> {
  protected _state: IEntityPollingModelState<T>;
  protected _interval?: NodeJS.Timeout;
  protected _updating: boolean;
  protected _returnSchema?: string;
  protected _filter?: Record<string, unknown>;
  protected _sort?: EntitySort<T>;
  protected _updateDelay: number;
  protected _pollingCallback?: PollingCallbackFn;

  constructor(options?: {returnSchema?: string, filter?: Record<string, unknown>, sort?: EntitySort<T>, updateDelay?: number, externalEntitiesContainer?: T[], pollingCallback?: PollingCallbackFn}) {
    this._interval        = undefined;
    this._updating        = false;
    this._returnSchema    = options?.returnSchema;
    this._filter          = options?.filter;
    this._sort            = options?.sort;
    this._updateDelay     = options?.updateDelay || 5;
    this._pollingCallback = options?.pollingCallback;
    this._state           = observable({
      entities: options?.externalEntitiesContainer ?? [],
    });
  }

  set returnSchema(value: string | undefined) {
    this._returnSchema = value;
    if (this._interval) {
      this.refreshUpdate();
    }
  }

  get returnSchema(): string | undefined{
    return this._returnSchema;
  }

  set filter(value: Record<string, unknown> | undefined) {
    this._filter = value;
    if (this._interval) {
      this.refreshUpdate();
    }
  }

  get filter(): Record<string, unknown> | undefined {
    return this._filter;
  }

  set sort(value: EntitySort<T> | undefined) {
    this._sort = value;
    if (this._interval) {
      this.refreshUpdate();
    }
  }

  get sort(): EntitySort<T> | undefined {
    return this._sort;
  }

  set updateDelay(value: number) {
    this._updateDelay = value;
    if (this._interval) {
      this.refreshUpdate();
    }
  }

  get updateDelay(): number {
    return this._updateDelay;
  }

  set entities(value: T[]) {
    this._state.entities.length = 0;
    this._state.entities.push(...value);
  }

  get entities(): T[] {
    return this._state.entities;
  }

  get entityCount(): number {
    return this.entities.length;
  }

  protected getPollingCallback(): () => Promise<void> {
    return async () => {
      this.entities = await this.getEntities();
      this._pollingCallback?.();
    };
  }

  protected async getEntities(): Promise<T[]> {
    const entities = await this._getEntities();
    entities.sort && entities.sort(this.sort);
    return entities;
  }

  protected abstract _getEntities(): Promise<T[]>

  reset() {
    this.stopUpdate();
    this.entities = [];
  }

  async startUpdate() {
    if (this._updating || this._interval) return;
    this._updating = true;
    const delay    = this.updateDelay * 1000;
    const callback = this.getPollingCallback();
    this.entities  = await this.getEntities();
    this._interval = setInterval(callback, delay);
    this._updating = false;
  }

  stopUpdate() {
    if (!this._interval) {
      return;
    }
    clearInterval(this._interval);
    this._interval = undefined;
  }

  async refreshUpdate() {
    this.stopUpdate();
    await this.startUpdate();
  }
}

export default abstract class EntityPollingService<T, TPollingModel extends EntityPollingModel<T>> {
  pollingModels: EntityPollingModel<T>[];

  constructor() {
    this.pollingModels = [];
  }

  createPollingModel(options?: {returnSchema?: string, filter?: any, sort?: EntitySort<T>, updateDelay?: number, externalEntitiesContainer?: T[], pollingCallback?: PollingCallbackFn}): TPollingModel {
    const newPollingModel = this._createPollingModel(options);

    this.pollingModels.push(newPollingModel);
    return newPollingModel;
  }

  protected abstract _createPollingModel(options?: {returnSchema?: string, filter?: Record<string, unknown>, sort?: EntitySort<T>, updateDelay?: number, externalEntitiesContainer?: T[], pollingCallback?: PollingCallbackFn}): TPollingModel

  stopUpdates() {
    this.pollingModels.forEach(m => {
      m.stopUpdate();
    });
  }
}
